import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, BarChart3, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { flattenReportRows, sortReportRows } from '@/lib/reporting/engine';
import { formatReportValue } from '@/lib/reporting/format';
import { formatPeriodLabel } from '@/lib/reporting/periods';
import { reportToCsv, type ExportLabels } from '@/lib/reporting/exportCsv';
import { reportToExcelXml } from '@/lib/reporting/exportExcel';
import { reportToPdf } from '@/lib/reporting/exportPdf';
import { downloadBlob } from '@/lib/reporting/format';
import { METRIC_BY_ID } from '@/lib/reporting/metrics';
import type { ReportResult, ReportRow } from '@/lib/reporting/types';
import { TABLE_CELL_TYPE, TABLE_HEADER_TYPE, UI_MUTED, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';

interface ReportResultViewProps {
  result: ReportResult;
  contextLabel?: string;
}

function identityCell(row: ReportRow, columnId: string): string {
  if (columnId === 'practitioner') {
    if (row.grouping === 'service' && row.depth > 0) return '';
    return row.label;
  }
  if (columnId === 'service') {
    if (row.grouping === 'practitioner' && row.depth === 0) return '';
    return row.label;
  }
  return '';
}

export function ReportResultView({ result, contextLabel }: ReportResultViewProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(result.rows.map((row) => row.id)),
  );
  const [sort, setSort] = useState<{ id: string; direction: 'asc' | 'desc' }>({
    id: 'label',
    direction: 'asc',
  });
  const [includePdfDetails, setIncludePdfDetails] = useState(false);

  const locale = i18n.language === 'da' ? 'da-DK' : 'en-GB';
  const dateLocale = i18n.language === 'da' ? 'da-DK' : 'en-GB';

  const labels: ExportLabels = useMemo(
    () => ({
      title: t('reports.types.practitioner.label'),
      practitioner: t('reports.columns.practitioner'),
      service: t('reports.columns.service'),
      summary: t('reports.export.summary'),
      detail: t('reports.export.detail'),
      metricLabels: Object.fromEntries(
        result.metricIds.map((id) => [
          METRIC_BY_ID[id]?.labelKey ?? id,
          t(METRIC_BY_ID[id]?.labelKey ?? id),
        ]),
      ),
      columnLabels: {},
      detailHeaders: {
        date: t('reports.export.date'),
        practitioner: t('reports.columns.practitioner'),
        patient: t('reports.export.patient'),
        service: t('reports.columns.service'),
        billingMethod: t('reports.export.billing_method'),
        clipsRedeemed: t('reports.export.clips_redeemed'),
        revenuePerClip: t('reports.export.revenue_per_clip'),
        recognizedRevenue: t('reports.export.recognized_revenue'),
        commissionPercent: t('reports.metrics.commission_percentage.label'),
        commissionAmount: t('reports.metrics.commission_amount.label'),
        sourceType: t('reports.export.source_type'),
      },
      pdf: {
        brand: 'Clinio',
        clinicLabel: t('reports.export.clinic'),
        generatedLabel: t('reports.export.generated'),
        periodLabel: t('reports.export.period'),
        practitionerLabel: t('reports.columns.practitioner'),
        allPractitioners: t('reports.filters.all_practitioners'),
        clinicTotal: t('reports.total'),
        keyFigures: t('reports.export.key_figures'),
        financialOverview: t('reports.export.financial_overview'),
        commissionOverview: t('reports.export.commission_overview'),
        revenueByService: t('reports.export.revenue_by_service'),
        appointmentPerformance: t('reports.export.appointment_performance'),
        patientPerformance: t('reports.export.patient_performance'),
        patientRetention: t('reports.export.patient_retention'),
        servicePerformance: t('reports.export.service_performance'),
        practitionerComparison: t('reports.export.practitioner_overview'),
        practitionerOverview: t('reports.export.practitioner_overview'),
        otherMetrics: t('reports.export.other_metrics'),
        retentionNote: t('reports.retention_footnote', { days: result.retentionFollowUpDays }),
        punchCard: t('reports.export.punch_card'),
        invoice: t('reports.export.invoice'),
        includeDetails: t('reports.export.include_details'),
        performanceTitle: t('reports.export.performance_title'),
        commission: t('reports.export.commission'),
        commissionUnconfigured: t('reports.export.commission_unconfigured'),
        detailedRecords: t('reports.export.detailed_treatment_records'),
        serviceMix: t('reports.export.service_mix'),
        footerReport: t('reports.export.footer_report'),
        pageLabel: t('reports.export.page'),
        pageOf: t('reports.export.page_of'),
        clip: t('reports.export.clip'),
      },
    }),
    [result.metricIds, t],
  );

  const sortedRows = useMemo(
    () => sortReportRows(result.rows, sort.id === 'label' ? 'label' : sort.id, sort.direction),
    [result.rows, sort],
  );
  const visible = flattenReportRows(sortedRows, expanded);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportFile = (kind: 'csv' | 'xls' | 'pdf') => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (kind === 'csv') {
      downloadBlob(
        new Blob([reportToCsv(result, labels)], { type: 'text/csv;charset=utf-8' }),
        `practitioner-report-${stamp}.csv`,
      );
      return;
    }
    if (kind === 'xls') {
      downloadBlob(
        new Blob([reportToExcelXml(result, labels)], { type: 'application/vnd.ms-excel' }),
        `practitioner-report-${stamp}.xls`,
      );
      return;
    }
    downloadBlob(
      new Blob(
        [
          new Uint8Array(
            reportToPdf(result, labels, {
              includeDetailedRecords: includePdfDetails,
              locale,
            }),
          ),
        ],
        { type: 'application/pdf' },
      ),
      `practitioner-report-${stamp}.pdf`,
    );
  };

  const handleSort = (columnId: string) => {
    setSort((prev) =>
      prev.id === columnId
        ? { id: columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { id: columnId, direction: 'asc' },
    );
  };

  const sortIcon = (columnId: string) => {
    if (sort.id !== columnId) return <ArrowUpDown className="ml-1 inline h-3 w-3 text-ds-muted" />;
    return sort.direction === 'asc' ? (
      <ArrowUp className="ml-1 inline h-3 w-3 text-ds-teal" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3 text-ds-teal" />
    );
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className={UI_SECTION}>{t('reports.results')}</h2>
          <p className={UI_MUTED}>
            {contextLabel ?? formatPeriodLabel(result.period, dateLocale)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className={`flex items-center gap-2 ${UI_MUTED}`}>
            <input
              type="checkbox"
              className="rounded border-ds-field text-ds-teal focus:ring-ds-teal"
              checked={includePdfDetails}
              onChange={(event) => setIncludePdfDetails(event.target.checked)}
            />
            {t('reports.export.include_details')}
          </label>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => exportFile('csv')}>
              <Download className="mr-1 h-3.5 w-3.5" />
              {t('reports.export.csv')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportFile('xls')}>
              <Download className="mr-1 h-3.5 w-3.5" />
              {t('reports.export.excel')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportFile('pdf')}>
              <Download className="mr-1 h-3.5 w-3.5" />
              {t('reports.export.pdf')}
            </Button>
          </div>
        </div>
      </div>

      {result.summary.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {result.summary.map((card) => {
            const metric = METRIC_BY_ID[card.metricId];
            return (
              <div
                key={card.metricId}
                className="rounded-card bg-ds-card px-s4 py-s3 shadow-card"
              >
                <p className={UI_SECONDARY}>{t(metric?.labelKey ?? card.metricId)}</p>
                <p className="mt-1 text-xl font-semibold text-ds-text">
                  {formatReportValue(card.value, metric?.format ?? 'text', result.currency, locale)}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {result.empty ? (
        <Card>
          <EmptyState icon={<BarChart3 className="h-7 w-7" />} title={t('reports.empty')} />
        </Card>
      ) : (
        <Card className="p-0 nav:p-0">
          <div className="overflow-x-auto">
          <table className="w-full min-w-0 border-collapse">
            <thead>
              <tr>
                {result.columns.map((column) => (
                  <th
                    key={column.id}
                    className={cn(TABLE_HEADER_TYPE, 'whitespace-nowrap border-b border-ds-line px-s3 pb-s2 text-left')}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        handleSort(
                          column.id === 'practitioner' || column.id === 'service'
                            ? 'label'
                            : column.id,
                        )
                      }
                      className="inline-flex items-center"
                    >
                      {column.id === 'practitioner'
                        ? t('reports.columns.practitioner')
                        : column.id === 'service'
                          ? t('reports.columns.service')
                          : t(column.label)}
                      {sortIcon(
                        column.id === 'practitioner' || column.id === 'service'
                          ? 'label'
                          : column.id,
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const hasChildren = row.children.length > 0;
                const isOpen = expanded.has(row.id);
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-ds-line',
                      row.grouping === 'subtotal' &&
                        'bg-ds-sunk font-medium',
                      row.depth === 0 &&
                        row.grouping !== 'total' &&
                        'font-medium text-ds-text',
                    )}
                  >
                    {result.columns.map((column, index) => {
                      const isTree = index === 0;
                      const metric = METRIC_BY_ID[column.metricId];
                      const content =
                        column.id === 'practitioner' || column.id === 'service'
                          ? identityCell(row, column.id)
                          : formatReportValue(
                              row.values[column.metricId] ?? null,
                              metric?.format ?? 'text',
                              result.currency,
                              locale,
                            );
                      return (
                        <td
                          key={column.id}
                          className={cn(
                            TABLE_CELL_TYPE,
                            'whitespace-nowrap border-b border-ds-line px-s3 py-s3',
                            isTree && row.depth > 0 && 'pl-8',
                          )}
                        >
                          {isTree && hasChildren ? (
                            <button
                              type="button"
                              onClick={() => toggle(row.id)}
                              className="inline-flex items-center gap-1"
                            >
                              {isOpen ? (
                                <ChevronDown className="h-3.5 w-3.5 text-ds-muted" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-ds-muted" />
                              )}
                              {content}
                            </button>
                          ) : (
                            content
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="bg-ds-teal-tint font-semibold text-ds-text">
                {result.columns.map((column, index) => {
                  const metric = METRIC_BY_ID[column.metricId];
                  const content =
                    column.id === 'practitioner' || column.id === 'service'
                      ? index === 0
                        ? result.totals.label
                        : ''
                      : formatReportValue(
                          result.totals.values[column.metricId] ?? null,
                          metric?.format ?? 'text',
                          result.currency,
                          locale,
                        );
                  return (
                    <td key={column.id} className="whitespace-nowrap px-s3 py-s3">
                      {content}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
          </div>
        </Card>
      )}

      <p className={UI_MUTED}>
        {t('reports.retention_footnote', { days: result.retentionFollowUpDays })}
      </p>
    </section>
  );
}
